import React, { useMemo } from 'react';
import { Card, Col, Row, Space, Statistic, Table } from 'antd';
import {
  CheckCircleTwoTone,
  DashboardOutlined,
  EnvironmentTwoTone,
  LineChartOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { getRouteTableColumns, routeTablePagination } from './routeTableColumns';
import type { Route, RouteOverviewMetrics } from '../../Types/Summary.types';
import { useAppDispatch } from '../../store/hooks';

type RoutePerformanceOverviewProps = {
  routeOverviewMetrics: RouteOverviewMetrics;
  totalActualDistance: number;
  routes: Route[];
};

const RoutePerformanceOverview = ({
  routeOverviewMetrics: {
    delivery_success_rate,
    total_actual_deliveries,
    total_cancelled_deliveries,
    total_planned_deliveries,
    total_skipped_deliveries,
  },
  totalActualDistance,
  routes,
}: RoutePerformanceOverviewProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const columns = useMemo(() => getRouteTableColumns(dispatch), [dispatch]);

  const overviewStats = [
    {
      key: 'planned',
      icon: <EnvironmentTwoTone />,
      label: 'Total Planned Deliveries',
      value: Number(total_planned_deliveries),
      color: '#1890ff',
    },
    {
      key: 'completed',
      icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      label: 'Total Delivery Completed',
      value: Number(total_actual_deliveries),
      color: '#52c41a',
    },
    {
      key: 'successRate',
      icon: <LineChartOutlined className="c-orange" />,
      label: 'Total Success Rate',
      value: Number(delivery_success_rate) * 100,
      suffix: '%',
      color: '#faad14',
    },
    {
      key: 'cancelled',
      icon: <StopOutlined className="c-red" />,
      label: 'Total Cancelled Deliveries',
      value: total_cancelled_deliveries,
      color: '#f5222d',
    },
    {
      key: 'skipped',
      icon: <StopOutlined className="c-orange-dark" />,
      label: 'Total Skipped Deliveries',
      value: Number(total_skipped_deliveries),
      color: '#fa541c',
    },
    {
      key: 'actualDistance',
      icon: <DashboardOutlined className="c-blue" />,
      label: 'Total Actual Distance',
      value: Number(totalActualDistance).toFixed(2),
      suffix: ' Km',
      color: '#1890ff',
    },
  ];

  return (
    <Row gutter={[16, 16]} className="pd-half-rem min-w-800">
      <Col span={24}>
        <Card className="modern-card h-100" title="Route Performance Overview">
          <Row gutter={[24, 24]} justify="space-around" align="middle" className="mg-b-25">
            {overviewStats.map(({ key, icon, label, value, suffix, color }) => (
              <Col xs={24} sm={12} md={8} key={key}>
                <Statistic
                  title={
                    <Space>
                      {icon}
                      {label}
                    </Space>
                  }
                  value={value}
                  suffix={suffix}
                  valueStyle={{ color }}
                />
              </Col>
            ))}
          </Row>
          <Table<Route>
            dataSource={routes}
            columns={columns}
            className="mg-t-15"
            bordered
            pagination={routeTablePagination}
            rowKey={(record) => record.trip_id}
            scroll={{ x: 1000 }}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default RoutePerformanceOverview;
